import * as React from "react";
import { withTheme } from "styled-components";

import { definedMap } from "@glideapps/ts-necessities";

import {
    ImageSource,
    imageSourceUrl,
    canMakeUrl,
    DropImageHandler,
    MapTheme,
} from "common/components/portable-renderers";
import {
    GridItemStyle,
    GridItemImageStyle,
    GridItemTitleStyle,
    GridItemSubtitleStyle,
    GridImageWrapper,
    GridItemInitials,
    InitialsText,
} from "./GridItemStyle";
import { defined } from "common/Support";
import { AutoSizer } from "react-virtualized/dist/es/AutoSizer";
import { ImageAspectRatio } from "common/generator/components/image-types";
import { pageMarginMedium, RuntimeTheme } from "../../lib/theme/theme";
import { scaleImageWidthToHeight } from "../../lib/image-utils";
import { OnClickHandler } from "hooks/use-on-click-from-action";
import { AppEnvironment } from "common/components/types";

interface Props {
    readonly appEnvironment: AppEnvironment;
    readonly title?: string;
    readonly subtitle?: string;
    readonly imageSource?: ImageSource;
    readonly size: ImageAspectRatio;
    readonly fallbackInitials?: string;
    readonly onClick?: OnClickHandler;
    readonly columnWidth?: number;
    readonly onDropImage?: DropImageHandler;
    readonly theme: RuntimeTheme;
}

interface State {
    url: string;
}

class GridItem extends React.Component<Props, State> {
    public state: State = {
        url: "",
    };

    private async updateUrl(imageSource: ImageSource, width: number, height: number, zoom: number) {
        const theme = this.props.theme.mapTheme === "light" ? MapTheme.Light : MapTheme.Dark;
        const url = await imageSourceUrl(this.props.appEnvironment, imageSource, width, height, zoom, theme);
        if (url !== undefined && url !== this.state.url) {
            this.setState({ url });
        }
    }

    private ImageWithFallback = (props: {
        imageSource: ImageSource | undefined;
        size: ImageAspectRatio;
        fallback: React.ReactNode;
        columnWidth: number | undefined;
    }) => {
        const { columnWidth, size, imageSource, fallback } = props;
        const url = this.state.url;
        if (columnWidth === undefined) {
            return (
                <AutoSizer disableHeight={true}>
                    {(outerProps: { width: number }) => {
                        const width = outerProps.width;
                        const height = scaleImageWidthToHeight(width, size);
                        if (canMakeUrl(imageSource)) {
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this.updateUrl(defined(imageSource), width, height, 15);
                        }
                        const inner = (
                            <GridItemImageStyle onDropImage={this.props.onDropImage} src={url} alternate={fallback} />
                        );
                        return (
                            <GridImageWrapper height={height} width={width}>
                                {inner}
                            </GridImageWrapper>
                        );
                    }}
                </AutoSizer>
            );
        } else {
            const width = columnWidth - 2 * pageMarginMedium;
            const height = scaleImageWidthToHeight(width, size);
            if (canMakeUrl(imageSource)) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.updateUrl(defined(imageSource), width, height, 15);
            }
            const inner = <GridItemImageStyle onDropImage={this.props.onDropImage} src={url} alternate={fallback} />;
            return (
                <GridImageWrapper height={height} width={width}>
                    {inner}
                </GridImageWrapper>
            );
        }
    };

    private onClick = () => defined(this.props.onClick)(false);

    public render(): React.ReactNode {
        const { title, subtitle, imageSource, size, fallbackInitials, onClick, columnWidth } = this.props;

        const fallback = definedMap(fallbackInitials, f => (
            <GridItemInitials>
                <InitialsText>{f}</InitialsText>
            </GridItemInitials>
        ));

        return (
            <GridItemStyle onClick={onClick === undefined ? undefined : this.onClick}>
                <this.ImageWithFallback
                    fallback={fallback}
                    imageSource={imageSource}
                    columnWidth={columnWidth}
                    size={size}
                />
                <GridItemTitleStyle>{title}</GridItemTitleStyle>
                <GridItemSubtitleStyle>{subtitle}</GridItemSubtitleStyle>
            </GridItemStyle>
        );
    }
}

export default withTheme(GridItem);
